import { SettingsType, type SettingInfoDto } from './pcfSettingsTypes';

export type BoltCalculationTypeValue = 'GasketBased' | 'BoltBased';
export type SystemGroupingPreferenceValue = 'BySystemTypeName' | 'BySystemName' | 'ByParameter';
export type LegacyIsoTypeValue = 1 | 2 | 3;

export function extractApiErrorDetails(error: unknown): { status?: number; message: string } {
  const maybe = error as any;
  const status = typeof maybe?.response?.status === 'number' ? maybe.response.status : undefined;
  const payload = maybe?.response?.data;
  const message =
    (typeof payload?.message === 'string' && payload.message) ||
    (typeof payload?.error === 'string' && payload.error) ||
    (typeof payload === 'string' && payload) ||
    (typeof maybe?.message === 'string' && maybe.message) ||
    'Unknown API error';
  return { status, message };
}

export function extractApiErrorDebugDetails(error: unknown): Record<string, unknown> {
  const maybe = error as any;
  const { status, message } = extractApiErrorDetails(error);
  const responseData = maybe?.response?.data;
  const normalizedResponseData =
    typeof responseData === 'string' && responseData.length > 2000
      ? `${responseData.slice(0, 2000)}...`
      : responseData ?? null;

  return {
    status: status ?? null,
    statusText: maybe?.response?.statusText ?? null,
    message,
    responseData: normalizedResponseData,
    code: maybe?.code ?? null,
    method: maybe?.config?.method ?? null,
    url: maybe?.config?.url ?? null,
  };
}

export function isAuthFailureError(error: unknown): boolean {
  const { status } = extractApiErrorDetails(error);
  return status === 401 || status === 403;
}

export function shouldFallbackFromPutToPost(error: unknown): boolean {
  const { status, message } = extractApiErrorDetails(error);
  if (status === 404) {
    return true;
  }

  if (status === 400) {
    const normalized = message.toLowerCase();
    if (
      normalized.includes('not found') ||
      normalized.includes("doesn't exist") ||
      normalized.includes('does not exist') ||
      normalized.includes('no settings')
    ) {
      return true;
    }
  }

  return false;
}

export function shouldFallbackFromPostToPut(error: unknown): boolean {
  const { status, message } = extractApiErrorDetails(error);
  if (status === 404 || status === 405) {
    return true;
  }

  if (status === 400 || status === 409) {
    const normalized = message.toLowerCase();
    if (
      normalized.includes('already exists') ||
      normalized.includes('duplicate') ||
      normalized.includes('conflict')
    ) {
      return true;
    }
  }

  return false;
}

export function normalizeProfileName(name: string | undefined, fallback: string): string {
  const normalized = (name || '').trim();
  return normalized.length > 0 ? normalized : fallback;
}

export function normalizeScopedProfileName(
  name: string | undefined,
  _settingsType: SettingsType,
  fallback: string
): string {
  return normalizeProfileName(name, fallback);
}

export function normalizePcfToken(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function normalizeSettingsType(
  raw: unknown,
  fallback: SettingsType = SettingsType.User
): SettingsType {
  if (typeof raw === 'number') {
    if (raw === SettingsType.Company) return SettingsType.Company;
    if (raw === SettingsType.User) return SettingsType.User;
    return fallback;
  }

  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (normalized === '0' || normalized === 'company') return SettingsType.Company;
    if (normalized === '1' || normalized === 'user') return SettingsType.User;
  }

  return fallback;
}

export function getProfileKey(name: string, settingsType: SettingsType): string {
  return `${name}::${settingsType}`;
}

const getProfileTimestamp = (value: string | undefined): number => {
  if (!value) {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function deduplicateSettingsProfiles(profiles: SettingInfoDto[]): SettingInfoDto[] {
  const byKey = new Map<string, SettingInfoDto>();

  profiles.forEach((profile) => {
    const key = getProfileKey(profile.name, profile.settingsType).toLowerCase();
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, profile);
      return;
    }

    const existingChanged = getProfileTimestamp(existing.changedOn);
    const incomingChanged = getProfileTimestamp(profile.changedOn);
    const existingCreated = getProfileTimestamp(existing.createdOn);
    const incomingCreated = getProfileTimestamp(profile.createdOn);

    if (
      incomingChanged > existingChanged ||
      (incomingChanged === existingChanged && incomingCreated > existingCreated)
    ) {
      byKey.set(key, profile);
    }
  });

  return Array.from(byKey.values());
}

export function getSettingsTypeWireVariants(
  settingsType: SettingsType
): Array<SettingsType | 'Company' | 'User'> {
  return settingsType === SettingsType.Company
    ? [SettingsType.Company, 'Company']
    : [SettingsType.User, 'User'];
}

export function isRetryableSettingsTypeContractError(error: unknown): boolean {
  const { status, message } = extractApiErrorDetails(error);
  if (status === 400 || status === 422 || status === 500) {
    const normalized = message.toLowerCase();
    return (
      normalized.includes('settingstype') ||
      normalized.includes('enum') ||
      normalized.includes('cannot convert') ||
      normalized.includes('validation') ||
      normalized.includes('model')
    );
  }

  return false;
}

export function parseJson<T>(value: string | null | undefined, fallback: T): T {
  if (!value) {
    return fallback;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export function normalizeAttributeType(
  raw: unknown
): 'Parameter' | 'BomColumn' | 'Attribute' | 'Formula' {
  if (typeof raw === 'number') {
    switch (raw) {
      case 1:
        return 'Parameter';
      case 2:
        return 'Attribute';
      case 3:
        return 'BomColumn';
      default:
        return 'Parameter';
    }
  }

  if (typeof raw !== 'string') {
    return 'Parameter';
  }

  const normalized = raw.trim();
  switch (normalized) {
    case '1':
      return 'Parameter';
    case '2':
      return 'Attribute';
    case '3':
      return 'BomColumn';
    case 'BomColumn':
    case 'Attribute':
    case 'Formula':
    case 'Parameter':
      return normalized;
    default:
      return 'Parameter';
  }
}

export function normalizeBomParameterType(raw: unknown): 1 | 2 | 3 {
  const unwrapped = unwrapLegacyValue(raw);
  if (typeof unwrapped === 'number') {
    if (unwrapped === 2) return 2;
    if (unwrapped === 3) return 3;
    return 1;
  }

  if (typeof unwrapped === 'string') {
    const normalized = unwrapped.trim().toLowerCase();
    if (normalized === '2' || normalized === 'systemparameters' || normalized === 'system') return 2;
    if (normalized === '3' || normalized === 'bomparameters' || normalized === 'bom') return 3;
  }

  return 1;
}

export function getTextValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

export function normalizeBoltCalculationType(
  raw: unknown,
  fallback: BoltCalculationTypeValue = 'GasketBased'
): BoltCalculationTypeValue {
  if (typeof raw === 'number') {
    return raw === 1 ? 'BoltBased' : 'GasketBased';
  }

  if (typeof raw !== 'string') {
    return fallback;
  }

  const normalized = raw.trim().toLowerCase();
  if (normalized === '1' || normalized === 'boltbased') {
    return 'BoltBased';
  }
  if (normalized === '0' || normalized === 'gasketbased') {
    return 'GasketBased';
  }
  return fallback;
}

// Numeric values follow the C# SystemGrouping enum order (PcfConfig.cs):
// 0 = BySystemTypeName, 1 = BySystemName, 2 = ByParameter. Serializers that
// ignore the string-enum converter persist the raw number, so every value of
// the enum must round-trip — anything unrecognized falls back instead of
// collapsing to a system-name mode.
export function normalizeSystemGroupingPreference(
  raw: unknown,
  fallback: SystemGroupingPreferenceValue = 'BySystemName'
): SystemGroupingPreferenceValue {
  if (typeof raw === 'number') {
    if (raw === 0) {
      return 'BySystemTypeName';
    }
    if (raw === 1) {
      return 'BySystemName';
    }
    if (raw === 2) {
      return 'ByParameter';
    }
    return fallback;
  }

  if (typeof raw !== 'string') {
    return fallback;
  }

  const normalized = raw.trim().toLowerCase();
  if (normalized === '0' || normalized === 'bysystemtypename') {
    return 'BySystemTypeName';
  }
  if (normalized === '1' || normalized === 'bysystemname') {
    return 'BySystemName';
  }
  if (normalized === '2' || normalized === 'byparameter') {
    return 'ByParameter';
  }
  return fallback;
}

export function toLegacyIsoType(raw: unknown): LegacyIsoTypeValue {
  const normalized = normalizeAttributeType(raw);
  if (normalized === 'BomColumn') {
    return 3;
  }
  if (normalized === 'Attribute' || normalized === 'Formula') {
    return 2;
  }
  return 1;
}

export function unwrapLegacyValue(value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    'Value' in (value as Record<string, unknown>)
  ) {
    return (value as { Value?: unknown }).Value;
  }

  return value;
}

export function convertLegacyIsoArrayToMap(entries: any[]): Record<string, any[]> {
  const grouped: Record<string, any[]> = {};

  entries.forEach((entry, index) => {
    const objectType = getTextValue(unwrapLegacyValue(entry?.ObjectType), 'ALL') || 'ALL';
    const pcfName = getTextValue(unwrapLegacyValue(entry?.PcfName));
    const parName = getTextValue(unwrapLegacyValue(entry?.ParName));
    const script = getTextValue(unwrapLegacyValue(entry?.Script));
    const cSort = String(unwrapLegacyValue(entry?.CSort) ?? '');
    const id = unwrapLegacyValue(entry?.Id);

    const mapped = {
      id: typeof id === 'number' ? id : Number(id ?? index + 1),
      pcfName,
      parName,
      script,
      isoType: normalizeAttributeType(unwrapLegacyValue(entry?.IsoType)),
      cSort,
    };

    if (!grouped[objectType]) {
      grouped[objectType] = [];
    }
    grouped[objectType].push(mapped);
  });

  return grouped;
}

export function getFormFieldValue(raw: unknown): string {
  if (typeof raw === 'string') {
    return raw;
  }

  if (raw && typeof raw === 'object' && 'value' in (raw as Record<string, unknown>)) {
    const value = (raw as { value?: unknown }).value;
    return typeof value === 'string' ? value : String(value ?? '');
  }

  return '';
}

export type NamedParameterPair = {
  name: string;
  value: string;
};

export function upsertNamedParameter(
  target: Array<{ name: string; value: string }>,
  name: string,
  value: string
): void {
  const normalizedName = name.trim().toLowerCase();
  if (!normalizedName) {
    return;
  }

  const incomingValue = value ?? '';
  const existingIndex = target.findIndex((entry) => entry.name.trim().toLowerCase() === normalizedName);
  if (existingIndex >= 0) {
    const existingValue = typeof target[existingIndex].value === 'string'
      ? target[existingIndex].value
      : '';
    const nextValue = incomingValue.trim().length > 0 ? incomingValue : existingValue;

    target[existingIndex] = {
      ...target[existingIndex],
      name,
      value: nextValue,
    };
    return;
  }

  target.push({ name, value: incomingValue });
}

export function normalizeParameterLookupValue(value: string): string {
  return value.trim().toLowerCase();
}

export function resolvePipeexDefaultParameterValue(
  value: string,
  availableParameterNames: Set<string>
): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '';
  }

  if (trimmed.startsWith('#')) {
    return trimmed;
  }

  if (availableParameterNames.size === 0) {
    return trimmed;
  }

  return availableParameterNames.has(normalizeParameterLookupValue(trimmed)) ? trimmed : '';
}

export function sanitizeResolvedRevitParameterValue(
  value: string,
  availableParameterNames: Set<string>
): string {
  // The catalog only lists parameters bound in the project or seen on sampled
  // elements, so Revit built-ins and free-typed names are usually absent.
  // Blanking them here destroyed user selections on every profile save;
  // availability feedback lives in mapping readiness instead.
  void availableParameterNames;

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return '';
  }

  return value;
}
